import {useCartService} from "./useCartService";
import {useArtwork, useDesignServiceId, useDimensions, usePromotionCode, useWarrantyId} from "../../redux/stateHooks";
import {useWarrantiesQuery} from "../useWarrantiesQuery";
import {useDesignServiceQuery} from "../useDesignServiceQuery";
import {CartItemResourceType} from "../../api/cart";
import {GPAWebDataTypesResourceType} from "../../api/openApi/cartService";
import {useQuantity} from "../useQuantity";
import {useAppContext} from "../useAppContext";

/**
 * A hook built on top of createItem which pulls in all the data needed to create a cart item.
 * This is used for regular add to cart buttons and not cross-sell items.
 * @constructor
 */
export const useCreateItem: () => { createItem: (artworkId?: number | null) => Promise<void> } = () => {
    // Get the product id for the current product
    const {productId} = useAppContext();

    // Get the quantity, artwork, and dimensions for the current product
    const {quantity} = useQuantity();
    const artwork = useArtwork();
    const dimensions = useDimensions();

    // Get the warranty data for the current product
    const warrantyId = useWarrantyId()
    const {model: {warranties}} = useWarrantiesQuery()
    const warranty = warranties.find(warranty => warranty.id === warrantyId)

    // Get the design service data for the current product
    const designServiceId = useDesignServiceId()
    const {model: {price: designServicePrice}} = useDesignServiceQuery()

    // Get the coupon code for the current product
    const promotionCode = usePromotionCode()

    // Get the createItem function from the cart service
    const {createItem} = useCartService();

    return {
        createItem: async (artworkId?: number | null) => {
            // Create the resources array
            const resources: CartItemResourceType[] = []

            // Add the user's selected design service as a resource
            if (designServiceId && designServicePrice) {
                resources.push({
                    resourceType: GPAWebDataTypesResourceType.DesignerService,
                    resourceId: designServiceId,
                    resourcePrice: designServicePrice
                })
            }

            // Add the user's selected warranty as a resource
            if (warrantyId && warranty?.price) {
                resources.push({
                    resourceType: GPAWebDataTypesResourceType.Warranty,
                    resourceId: warrantyId,
                    resourcePrice: warranty.price
                })
            }

            // Create the cart item
            await createItem({
                productId: productId,
                quantity: quantity,
                // If the caller passes null for artworkId, use null, otherwise use the artwork id from the artwork hook
                // Note: This logic is for notes only products, where an existing artworkId might exist from an add with notes
                // But then they remove the notes, we want the artwork id to be null and the notes to be empty.
                artworkId: artworkId === null ? null : artworkId ?? artwork?.id ?? null,
                artworkName: artwork?.name ?? null,
                artworkNotes: artwork?.notes ?? null,
                width: dimensions.width ?? null,
                height: dimensions.height ?? null,
                resources: resources,
                promotionCode: promotionCode
            });
        }
    }
}